import i18nFactory from '@yandex-int/i18n';
import { ApolloQueryResult } from 'apollo-client';
import { compact, mergeWith } from 'lodash';
import moment from 'moment';
import { isArray } from 'util';

import { PricePackagesEditQueryResponse } from '../../api/pricePackages/pricePackagesDataProvider';
import { DropdownPricePackageItem } from '../../components/DropdownPricePackageEditor/DropdownPricePackageEditor';
import { getPricePackagesFilterCriteria, PricePackagesCriteriaKey } from '../../declarations/filter/filter';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { getStrictExpressions } from '../../service/FilterHelpers/FilterHelpers';
import { PricePackagesHelpers } from '../../service/PricePackagesHelpers/PricePackagesHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { Region, RegionId } from '../../typings/common';
import { DatesIntervalEditorsState } from '../../typings/datesInterval';
import { EditPageItemNameEditorsState } from '../../typings/editPageItemNameEditor';
import {
    FilterExpression,
    FilterPartialExpression,
    PricePackagesQueryFilter,
    StatsQueryFilter
} from '../../typings/filter';
import {
    CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    CurrencyCodeInput,
    GdAddPricePackagesInput,
    GdGetPricePackagesInput,
    GdStatusApprove,
    GdStatusApproveInput,
    GdUpdatePricePackagesInput,
    GdViewType,
    GdViewTypeInput,
    GroupsQueryForEdit_client_adGroups_rowset_GdSmartAdGroup_campaign,
    PricePackagesAvailableForClient,
    PricePackagesQuery,
    PricePackagesQueryForEdit,
    PricePackagesSuggestQuery
} from '../../typings/graphql-grid';
import { CurrencyCode } from '../../typings/graphql-public-grid';
import {
    GEO_TYPES,
    GEO_TYPES_API,
    PriceGeoType,
    PricePackageEditData,
    PricePackageEditorValue,
    PricePackageId,
    PricePackagesData,
    PricePackagesForGroup,
    PricePackagesSourceParams
} from '../../typings/pricePackages';
import { RegionsEditorState } from '../../typings/regionsEditor';
import { FilterCriteriaMappings, textToPricePackagesFilterExpression } from '../filter/filter';

import * as keyset from './pricePackages.i18n';

const i18n = i18nFactory(keyset);

const mappings = new FilterCriteriaMappings<PricePackagesQueryFilter & StatsQueryFilter>();

const pricePackagesCriteriaMappings: {
    [key in PricePackagesCriteriaKey]: (expression: FilterExpression) => PricePackagesQueryFilter;
} = {
    name: mappings.contains('nameContains', 'nameNotContains'),
    id: mappings.includes('packageIdIn', 'packageIdIn')
};

const expressionToPricePackagesQueryFilter = (expression: FilterExpression): PricePackagesQueryFilter => {
    return pricePackagesCriteriaMappings[expression.criteriaKey](expression);
};

export const mapPricePackageFilterToApiFormat = (expressions?: FilterPartialExpression[]): PricePackagesQueryFilter => {
    expressions = expressions || [];
    const criteria = getPricePackagesFilterCriteria();

    return getStrictExpressions(expressions, criteria, textToPricePackagesFilterExpression)
        .map(expressionToPricePackagesQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export const mapPricePackagesQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<PricePackagesQuery>
): {
    data: PricePackagesData;
    reqId: string;
} => {
    const { reqId } = result.data;
    const { rowset = [], totalCount = 0 } = result.data.getPricePackages || {};

    return {
        data: {
            data: rowset.map(row => {
                const targetingFixed = row.targetingsFixed;
                const targetingCustom = row.targetingsCustom;

                return {
                    id: String(row.id),
                    title: row.title || '',
                    price: Number(row.price),
                    dateEnd: row.dateEnd,
                    dateStart: row.dateStart,
                    orderVolumeMax: Number(row.orderVolumeMax),
                    orderVolumeMin: Number(row.orderVolumeMin),
                    clientIds: compact(row.clientIds) || [],
                    currency: row.currency,
                    statusApprove: row.statusApprove || GdStatusApprove.NEW,
                    targetingsFixed: {
                        viewTypes: targetingFixed && targetingFixed.viewTypes ? targetingFixed.viewTypes : undefined,
                        geoType: targetingFixed && targetingFixed.geoType ? targetingFixed.geoType : undefined,
                        geo: targetingFixed && targetingFixed.geo ? targetingFixed.geo : undefined
                    },
                    targetingsCustom: {
                        behaviors: targetingCustom && targetingCustom.behaviors ? targetingCustom.behaviors : undefined,
                        geoType: targetingCustom && targetingCustom.geoType ? targetingCustom.geoType : undefined,
                        geo: targetingCustom && targetingCustom.geo ? targetingCustom.geo : undefined
                    },
                    lastUpdateTime: row.lastUpdateTime
                };
            }),
            total: totalCount,
            allItemsIds: rowset.map(row => String(row.id))
        },
        reqId
    };
};

export const mapSourceParamsToPricePackagesAndSuggestQueryVariables = (
    params: PricePackagesSourceParams
): GdGetPricePackagesInput => {
    const { filter } = params;
    const filterApiFormat = mapPricePackageFilterToApiFormat(filter);

    return {
        filter: {
            packageIdIn: filterApiFormat.packageIdIn
        }
    };
};

export const mapPricePackagesSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<PricePackagesSuggestQuery>
): {
    data: string[];
    reqId: string;
} => {
    const { rowset = [] } = result.data.getPricePackages || {};

    return {
        data: rowset.reduce((acc: string[], item) => {
            acc.push(item.title);

            return acc;
        }, []),
        reqId: result.data.reqId
    };
};

export const makePricePackageDataForCreation = (): Record<PricePackageId, PricePackageEditData> => {
    const id = `package-${new Date().getTime()}`;

    return {
        [id]: {
            id: id,
            title: getNewPackageName(),
            price: 0,
            dateEnd: '',
            dateStart: '',
            trackerUrl: '',
            clientIds: [],
            orderVolumeMin: 0,
            orderVolumeMax: 0,
            currency: CurrencyCode.RUB,
            statusApprove: GdStatusApprove.NEW,
            targetingsFixed: {
                viewTypes: [GdViewType.DESKTOP]
            },
            targetingsCustom: {
                behaviors: []
            },
            regions: {
                selectedMap: {},
                regionIds: [],
                isFixed: false
            },
            lastUpdateTime: '',
            errors: {}
        }
    };
};

export const mapPricePackagesEditData = (
    result: ApolloQueryResult<PricePackagesQueryForEdit>,
    regionTree: Region[]
): PricePackagesEditQueryResponse => {
    const { reqId } = result.data;

    if (!result.data.getPricePackages) {
        return {
            data: {},
            reqId
        };
    }

    return {
        data: result.data.getPricePackages.rowset.reduce((acc, pricePackage) => {
            const targetingsFixed = pricePackage.targetingsFixed;
            const targetingsCustom = pricePackage.targetingsCustom;
            const id = String(pricePackage.id);
            let regionIds: number[] = [];
            let regionIsFixed: boolean = false;
            let geoType;

            if (targetingsFixed && targetingsFixed.geo) {
                regionIds = targetingsFixed.geo.map(item => Number(item));
                geoType = targetingsFixed.geoType;
                regionIsFixed = true;
            } else if (targetingsCustom && targetingsCustom.geo) {
                regionIds = targetingsCustom.geo.map(item => Number(item));
                geoType = targetingsCustom.geoType;
            }
            const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, regionIds);

            acc[id] = {
                id: id,
                title: pricePackage.title,
                price: Number(pricePackage.price),
                dateEnd: pricePackage.dateEnd,
                dateStart: pricePackage.dateStart,
                trackerUrl: pricePackage.trackerUrl,
                orderVolumeMax: Number(pricePackage.orderVolumeMax),
                orderVolumeMin: Number(pricePackage.orderVolumeMin),
                clientIds: pricePackage.clientIds || [],
                currency: pricePackage.currency,
                statusApprove: pricePackage.statusApprove || GdStatusApprove.NEW,
                targetingsFixed: {
                    viewTypes: targetingsFixed && targetingsFixed.viewTypes ? targetingsFixed.viewTypes : undefined
                },
                targetingsCustom: {
                    behaviors: targetingsCustom && targetingsCustom.behaviors ? targetingsCustom.behaviors : undefined
                },
                regions: {
                    selectedMap: selectedMap,
                    regionIds: regionIds,
                    type: geoType ? GEO_TYPES[geoType] : undefined,
                    isFixed: regionIsFixed
                },
                lastUpdateTime: pricePackage.lastUpdateTime,
                errors: {}
            };

            return acc;
        }, {}),
        reqId
    };
};

export const mapPeriodsEditorsDataFromPricePackages = (
    pricePackages: Record<PricePackageId, PricePackageEditData>
): DatesIntervalEditorsState | undefined => {
    const pricePackagesIds = Object.keys(pricePackages);

    if (pricePackagesIds.length === 0) {
        return;
    }

    return pricePackagesIds.reduce((acc, pricePackageId) => {
        const currentValue = pricePackages[pricePackageId];
        const startTimestamp = currentValue.dateStart;
        const finishTimestamp = currentValue.dateEnd;

        const defaultDate = moment().startOf('day');
        const startDate = startTimestamp ? moment(startTimestamp) : defaultDate;
        const endDate = finishTimestamp ? moment(finishTimestamp) : defaultDate;

        return {
            ...acc,
            [pricePackageId]: {
                startDate,
                endDate
            }
        };
    }, {});
};

export const getPricePackagesValueFromEditors = (
    pricePackages: Record<PricePackageId, PricePackageEditData>,
    regionsEditor: Record<string, RegionsEditorState>,
    nameEditors: EditPageItemNameEditorsState,
    dateEditors: DatesIntervalEditorsState,
    regionTree?: Region[]
): Record<PricePackageId, PricePackageEditData> => {
    return Object.keys(pricePackages).reduce((acc, key) => {
        const pricePackage = pricePackages[key];
        const regions = regionsEditor[pricePackage.id];
        const nameEditor = selectEditPageItemNameEditorValue(
            nameEditors,
            PricePackagesHelpers.getPricePackageEditPageItemEditorId(pricePackage.id)
        );
        const dateEditor = dateEditors[pricePackage.id];
        const dateStart = dateEditor.startDate && dateEditor.startDate.format('YYYY-MM-DD');
        const dateEnd = dateEditor.endDate && dateEditor.endDate.format('YYYY-MM-DD');
        const viewTypes = pricePackage.targetingsFixed.viewTypes;
        let regionsIds: RegionId[] = [];

        if (regions && regions.isOpen) {
            if (regionTree) {
                regionsIds = RegionsHelpers.convertRegionsSelectedInfoToFlattenList(
                    RegionsHelpers.getSelectedRegionsInfo(regionTree, regions.selectedMap)
                );
            }
        } else {
            regionsIds = pricePackage.regions.regionIds;
        }

        acc[pricePackage.id] = {
            id: pricePackage.id,
            title: nameEditor.isVisible ? nameEditor.value : pricePackage.title,
            dateStart: dateStart,
            dateEnd: dateEnd,
            currency: pricePackage.currency,
            price: pricePackage.price,
            trackerUrl: pricePackage.trackerUrl,
            orderVolumeMin: pricePackage.orderVolumeMin,
            orderVolumeMax: pricePackage.orderVolumeMax,
            targetingsFixed: {
                viewTypes: viewTypes
            },
            targetingsCustom: {
                behaviors: pricePackage.targetingsCustom.behaviors || []
            },
            lastUpdateTime: pricePackage.lastUpdateTime,
            regions: {
                selectedMap: pricePackage.regions.selectedMap,
                regionIds: regionsIds,
                type: regions && regions.isOpen ? regions.geoType : pricePackage.regions.type,
                isFixed: regions && regions.isOpen ? regions.isFixed : pricePackage.regions.isFixed
            }
        };

        return acc;
    }, {});
};

export const mapPricePackageDataToEditInput = (
    pricePackages: Record<PricePackageId, PricePackageEditData>
): GdUpdatePricePackagesInput => {
    return {
        updateItems: Object.keys(pricePackages).map(key => {
            const pricePackage = pricePackages[key];
            const viewTypes = pricePackage.targetingsFixed.viewTypes;
            const isFixedGeo = pricePackage.regions.isFixed;
            const geo = pricePackage.regions.regionIds.map(id => String(id));
            const geoType = pricePackage.regions.type
                ? GEO_TYPES_API[pricePackage.regions.type]
                : GEO_TYPES_API[PriceGeoType.countries];

            return {
                id: pricePackage.id,
                title: pricePackage.title,
                dateStart: pricePackage.dateStart,
                dateEnd: pricePackage.dateEnd,
                currency: CurrencyCodeInput[pricePackage.currency],
                price: String(pricePackage.price),
                trackerUrl: pricePackage.trackerUrl,
                orderVolumeMin: String(pricePackage.orderVolumeMin),
                orderVolumeMax: String(pricePackage.orderVolumeMax),
                targetingsFixed: {
                    viewTypes: viewTypes ? viewTypes.map(viewType => GdViewTypeInput[viewType]) : [],
                    behaviors: [],
                    geo: isFixedGeo ? geo : undefined,
                    geoType: isFixedGeo ? geoType : undefined
                },
                targetingsCustom: {
                    ...pricePackage.targetingsCustom,
                    geo: isFixedGeo ? undefined : geo,
                    geoType: isFixedGeo ? undefined : geoType
                },
                lastSeenUpdateTime: pricePackage.lastUpdateTime
            };
        })
    };
};

export const mapPricePackageDataToApi = (
    pricePackage: PricePackageEditData,
    status: GdStatusApprove
): GdUpdatePricePackagesInput => {
    const targetingsFixed = pricePackage.targetingsFixed;
    const targetingsCustom = pricePackage.targetingsCustom;

    return {
        updateItems: [
            {
                id: pricePackage.id,
                statusApprove: GdStatusApproveInput[status],
                targetingsFixed: {
                    behaviors: [],
                    viewTypes: targetingsFixed.viewTypes
                        ? targetingsFixed.viewTypes.map(viewType => GdViewTypeInput[viewType])
                        : [],
                    geo: targetingsFixed.geo ? targetingsFixed.geo : undefined,
                    geoType: targetingsFixed.geoType ? targetingsFixed.geoType : undefined
                },
                targetingsCustom: {
                    behaviors: targetingsCustom && targetingsCustom.behaviors ? targetingsCustom.behaviors : [],
                    geo: targetingsCustom && targetingsCustom.geo ? targetingsCustom.geo : undefined,
                    geoType: targetingsCustom && targetingsCustom.geoType ? targetingsCustom.geoType : undefined
                },
                lastSeenUpdateTime: pricePackage.lastUpdateTime
            }
        ]
    };
};

export const mapPricePackageDataToCreateInput = (
    pricePackages: Record<PricePackageId, PricePackageEditData>
): GdAddPricePackagesInput => {
    return {
        addItems: Object.keys(pricePackages).map(key => {
            const pricePackage = pricePackages[key];
            const viewTypes = pricePackage.targetingsFixed.viewTypes;
            const isFixedGeo = pricePackage.regions.isFixed;
            const geo = pricePackage.regions.regionIds.map(id => String(id));
            const geoType = pricePackage.regions.type
                ? GEO_TYPES_API[pricePackage.regions.type]
                : GEO_TYPES_API[PriceGeoType.countries];

            return {
                title: pricePackage.title,
                dateStart: pricePackage.dateStart,
                dateEnd: pricePackage.dateEnd,
                currency: CurrencyCodeInput[pricePackage.currency],
                price: String(pricePackage.price),
                trackerUrl: pricePackage.trackerUrl,
                orderVolumeMin: String(pricePackage.orderVolumeMin),
                orderVolumeMax: String(pricePackage.orderVolumeMax),
                targetingsFixed: {
                    viewTypes: viewTypes ? viewTypes.map(viewType => GdViewTypeInput[viewType]) : [],
                    behaviors: [],
                    geo: isFixedGeo ? geo : undefined,
                    geoType: isFixedGeo ? geoType : undefined
                },
                targetingsCustom: {
                    ...pricePackage.targetingsCustom,
                    geo: isFixedGeo ? undefined : geo,
                    geoType: isFixedGeo ? undefined : geoType
                }
            };
        })
    };
};

export const mapPricePackageDataToDropdownEditor = (
    pricePackages?: Array<PricePackageEditorValue>
): Array<DropdownPricePackageItem> | undefined => {
    if (!pricePackages) {
        return;
    }

    return pricePackages.map(pricePackage => {
        const { id, price, title, dateStart, dateEnd, orderVolumeMin, orderVolumeMax, currency } = pricePackage;
        return {
            id,
            price,
            title,
            dateStart,
            dateEnd,
            orderVolumeMin,
            orderVolumeMax,
            currency
        };
    });
};

export const mapPricePackagesEditorData = (
    result: ApolloQueryResult<PricePackagesAvailableForClient>
): Array<PricePackageEditorValue> => {
    const { getPricePackagesAvailableForClient } = result.data;

    if (!getPricePackagesAvailableForClient) {
        return [];
    }

    return getPricePackagesAvailableForClient.rowset.map(pricePackage => {
        const targetingsFixed = pricePackage.targetingsFixed;
        const targetingsCustom = pricePackage.targetingsCustom;
        const id = String(pricePackage.id);

        return {
            id: id,
            title: pricePackage.title || '',
            price: pricePackage.price || '',
            dateEnd: pricePackage.dateEnd,
            dateStart: pricePackage.dateStart,
            orderVolumeMax: pricePackage.orderVolumeMax,
            orderVolumeMin: pricePackage.orderVolumeMin,
            currency: pricePackage.currency,
            targetingsFixed: {
                viewTypes: targetingsFixed && targetingsFixed.viewTypes ? targetingsFixed.viewTypes : undefined
            },
            targetingsCustom: {
                behaviors: targetingsCustom && targetingsCustom.behaviors ? targetingsCustom.behaviors : undefined
            }
        };
    });
};

const getNewPackageName = (): string => {
    return i18n('new-package');
};

export const mapPricePackagesDataForGroup = (
    campaignData:
        | CampaignBasicDataForGroupEdit_client_campaigns_rowset
        | GroupsQueryForEdit_client_adGroups_rowset_GdSmartAdGroup_campaign
): PricePackagesForGroup | undefined => {
    switch (campaignData.__typename) {
        case 'GdPriceCampaign':
            const pricePackage = campaignData.pricePackage;
            if (!pricePackage) {
                return;
            }
            const targetingsFixed = pricePackage.targetingsFixed;

            return {
                id: String(pricePackage.id),
                title: pricePackage.title,
                isFixedGeo: Boolean(targetingsFixed && targetingsFixed.geo)
            };
        default:
            return;
    }
};
